import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "./lib/api";
import * as A from "./lib/api";
import { H1, H2, Sub, Card } from "./Pages";
import MediaUploader from "./components/MediaUploader";
import MediaGallery from "./components/MediaGallery";
import { Paperclip } from "lucide-react";

const useLoader = (fn, deps=[]) => {
  const [data, set] = useState(null);
  const [busy, setBusy] = useState(true);
  const reload = useCallback(async () => {
    setBusy(true); try { const r = await fn(); set(r.data); } finally { setBusy(false); }
  }, deps); // eslint-disable-line
  useEffect(() => { reload(); }, [reload]);
  return { data, busy, reload };
};

// ==================== PERSONAL DASHBOARD ====================
export function PersonalDashboardPage() {
  const { data, busy } = useLoader(() => api.get("/dashboard/personal"));
  if (busy || !data) return <div>Loading…</div>;
  const l = data.learning;
  const tile = (label, val, sub, color='var(--primary)') => (
    <Card>
      <div className="label-caps">{label}</div>
      <div className="font-display text-4xl mt-2" style={{color}}>{val}</div>
      {sub && <div className="text-xs mt-1" style={{color:'var(--muted)'}}>{sub}</div>}
    </Card>
  );
  return (
    <div data-testid="personal-dashboard">
      <H1>Dashboard 1 · Personal</H1>
      <Sub>{data.user.name} · {data.user.household} household — your private view. You control everything on this dashboard; collective activity needs collective approval.</Sub>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {tile("My Drafts", data.my_drafts_count, `${data.my_shared_drafts} shared`)}
        {tile("My Resolutions", data.my_resolutions, "drafted by you")}
        {tile("Learning", `${l.pct}%`, `${l.lessons_done}/${l.total_lessons} lessons · ${l.courses} courses`, 'var(--gold-hover)')}
        {tile("Council Room", data.council.unread || 0, `${data.council.mentions} mention(s)`, data.council.unread ? 'var(--overdue)' : 'var(--primary)')}
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <Card>
          <div className="label-caps mb-2">My Recent Drafts</div>
          {data.latest_drafts.length===0 && <div className="text-sm" style={{color:'var(--muted)'}}>No drafts yet. Head to <Link to="/studio" className="underline">the Studio</Link>.</div>}
          {data.latest_drafts.map(d => (
            <Link key={d.id} to={`/studio/${d.id}`} className="block py-2 border-b last:border-b-0" style={{borderColor:'var(--border)'}}>
              <div className="font-medium text-sm">{d.Title}</div>
              <div className="text-xs" style={{color:'var(--muted)'}}>{d.Visibility} · {(d.UpdatedAt||"").slice(0,16).replace('T',' ')}</div>
            </Link>
          ))}
        </Card>
        <Card>
          <div className="label-caps mb-2">Your Activity</div>
          {(data.recent_activity||[]).slice(0,10).map(a => (
            <div key={a.id} className="py-1.5 text-xs border-b last:border-b-0" style={{borderColor:'var(--border)'}}>
              <span className="font-mono">{(a.timestamp||"").slice(0,16).replace('T',' ')}</span> · {a.action}
            </div>
          ))}
          {(!data.recent_activity || data.recent_activity.length===0) && <div className="text-sm" style={{color:'var(--muted)'}}>No activity yet.</div>}
        </Card>
      </div>
      <div className="mt-8 flex gap-3">
        <Link to="/my-root" className="btn-outline">Edit My Root</Link>
        <Link to="/academy" className="btn-outline">Continue learning</Link>
        <Link to="/council" className="btn-primary">Open Council Room</Link>
      </div>
    </div>
  );
}

// ==================== PROFILE / NOTIFICATION PREFS ====================
export function ProfilePage() {
  const [prefs, setPrefs] = useState(null);
  const [me, setMe] = useState(null);
  const load = async () => { const r = await api.get("/me/notifications"); setPrefs(r.data); };
  const loadMe = async () => { try { const r = await A.me(); setMe(r.data); } catch (e) {} };
  useEffect(() => { load(); loadMe(); }, []);
  const onAvatar = async (asset) => {
    try { await A.media.setAvatar(asset.id); toast.success("Profile photo updated"); loadMe(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const save = async () => {
    const p = { notify_email: prefs.notify_email, notify_phone: prefs.notify_phone, slack_user_id: prefs.slack_user_id,
                enable_email: prefs.enable_email, enable_sms: prefs.enable_sms, enable_slack: prefs.enable_slack };
    await api.put("/me/notifications", p); toast.success("Saved");
  };
  const test = async () => { try { const r = await api.post("/me/notifications/test"); toast.success(`Test dispatched. Providers configured: ${Object.entries(r.data.providers).filter(([_,v])=>v).map(([k])=>k).join(", ")||"none"}`); } catch(e){ toast.error("Failed"); } };
  if (!prefs) return <div>Loading…</div>;
  const providers = prefs.providers || {};
  return (
    <div data-testid="profile-page">
      <H1>Your Profile & Notifications</H1>
      <Sub>Your primary trustee email (<code>@endigenumapex.org</code>) is internal only. Attach a secondary channel below to receive real notifications.</Sub>
      <Card className="mb-4">
        <div className="label-caps mb-2">Profile photo</div>
        <div className="flex items-center gap-4">
          {me?.avatar_media_id ? (
            <img src={A.media.url(me.avatar_media_id)} alt="avatar" className="w-20 h-20 rounded-full object-cover border" style={{ borderColor: "var(--border)" }} data-testid="avatar-img" />
          ) : (
            <div className="w-20 h-20 rounded-full flex items-center justify-center border font-display text-2xl" style={{ borderColor: "var(--border)", background: "var(--surface-2)", color: "var(--primary)" }} data-testid="avatar-placeholder">
              {(me?.name || "?").slice(0, 1)}
            </div>
          )}
          <MediaUploader contextType="profile" accept="image/*" label="Upload photo" onUploaded={onAvatar} testid="avatar-uploader" />
        </div>
      </Card>
      <Card className="mb-4">
        <div className="label-caps mb-2">Provider status (backend)</div>
        <div className="flex gap-2 flex-wrap">
          <span className={`chip ${providers.email?'chip-green':'chip-gray'}`}>Email: {providers.email ? "ready":"no key"}</span>
          <span className={`chip ${providers.sms?'chip-green':'chip-gray'}`}>SMS: {providers.sms ? "ready":"no key"}</span>
          <span className={`chip ${providers.slack?'chip-green':'chip-gray'}`}>Slack: {providers.slack ? "ready":"no key"}</span>
        </div>
        <div className="text-xs mt-2" style={{color:'var(--muted)'}}>The Records Officer must add RESEND_API_KEY, TWILIO_*, and SLACK_BOT_TOKEN to backend .env — until then, save your preferences and the portal will fanout as soon as keys arrive.</div>
      </Card>
      <Card className="mb-4">
        <div className="label-caps mb-2">Secondary contact</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><label className="text-xs">Personal email</label>
            <input value={prefs.notify_email||""} onChange={e=>setPrefs({...prefs, notify_email:e.target.value})} data-testid="pref-email" placeholder="you@personal.example"/></div>
          <div><label className="text-xs">Mobile phone (E.164, +1…)</label>
            <input value={prefs.notify_phone||""} onChange={e=>setPrefs({...prefs, notify_phone:e.target.value})} data-testid="pref-phone" placeholder="+15551234567"/></div>
          <div><label className="text-xs">Slack user ID (U0…)</label>
            <input value={prefs.slack_user_id||""} onChange={e=>setPrefs({...prefs, slack_user_id:e.target.value})} data-testid="pref-slack" placeholder="U01ABCDEFG"/>
            <div className="text-xs mt-1" style={{color:'var(--muted)'}}>Find via Slack profile menu → More → Copy member ID.</div></div>
        </div>
      </Card>
      <Card className="mb-4">
        <div className="label-caps mb-2">Channels I want</div>
        <label className="flex gap-2 py-1"><input type="checkbox" className="w-4" checked={!!prefs.enable_email} onChange={e=>setPrefs({...prefs, enable_email:e.target.checked})} data-testid="toggle-email"/> Email</label>
        <label className="flex gap-2 py-1"><input type="checkbox" className="w-4" checked={!!prefs.enable_sms} onChange={e=>setPrefs({...prefs, enable_sms:e.target.checked})} data-testid="toggle-sms"/> SMS</label>
        <label className="flex gap-2 py-1"><input type="checkbox" className="w-4" checked={!!prefs.enable_slack} onChange={e=>setPrefs({...prefs, enable_slack:e.target.checked})} data-testid="toggle-slack"/> Slack DM</label>
      </Card>
      <div className="flex gap-2">
        <button className="btn-primary" onClick={save} data-testid="pref-save">Save preferences</button>
        <button className="btn-outline" onClick={test} data-testid="pref-test">Send test notification</button>
      </div>
    </div>
  );
}

// ==================== COUNCIL ROOM ====================
export function CouncilPage() {
  const [state, setState] = useState({ channels: [], dms: [] });
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [text, setText] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newCh, setNewCh] = useState({ name: "", description: "" });
  const [pending, setPending] = useState([]);
  const [mediaMap, setMediaMap] = useState({});
  const scrollRef = useRef(null);

  const loadChannels = async () => { const r = await api.get("/council/channels"); setState(r.data); };
  const loadUsers = async () => { const r = await api.get("/users"); setUsers(r.data); };
  const loadMedia = async (cid) => {
    try { const r = await A.media.list("council", cid); const m = {}; r.data.forEach(a => { m[a.id] = a; }); setMediaMap(m); } catch (e) {}
  };
  const loadMessages = async (cid) => { if(!cid) return; const r = await api.get(`/council/channels/${cid}/messages`); setMessages(r.data.messages); loadMedia(cid); setTimeout(()=>scrollRef.current?.scrollTo(0, 999999), 60); };

  useEffect(() => { loadChannels(); loadUsers(); }, []);
  useEffect(() => { if (selected) { loadMessages(selected.id); setPending([]); } }, [selected]);

  // poll every 4s
  useEffect(() => {
    const t = setInterval(() => {
      if (selected) loadMessages(selected.id);
      loadChannels();
    }, 4000);
    return () => clearInterval(t);
  }, [selected]);

  const send = async () => {
    if ((!text.trim() && pending.length === 0) || !selected) return;
    await api.post(`/council/channels/${selected.id}/messages`, { text, attachments: pending.map(a => a.id) });
    setText(""); setPending([]); loadMessages(selected.id);
  };
  const onAttach = (asset) => { setPending(p => [...p, asset]); setMediaMap(m => ({ ...m, [asset.id]: asset })); };
  const react = async (mid, emoji) => { await api.post(`/council/messages/${mid}/react`, { emoji }); loadMessages(selected.id); };
  const openDM = async (uid) => { const r = await api.post(`/council/dm/${uid}`); setSelected(r.data); loadChannels(); };
  const createChannel = async () => {
    if(!newCh.name) return;
    const r = await api.post("/council/channels", newCh);
    setNewCh({name:"",description:""}); setShowNew(false); loadChannels(); setSelected(r.data);
  };

  return (
    <div data-testid="council-page" className="flex gap-4" style={{height:'calc(100vh - 110px)'}}>
      <div className="w-64 shrink-0 card p-3 overflow-y-auto">
        <div className="flex justify-between mb-2"><div className="label-caps">Channels</div><button className="text-xs underline" onClick={()=>setShowNew(true)} data-testid="new-channel-btn">+ New</button></div>
        {state.channels.map(c => (
          <button key={c.id} onClick={()=>setSelected(c)} data-testid={`ch-${c.name}`}
            className={`w-full text-left py-1.5 px-2 rounded flex justify-between items-center ${selected?.id===c.id ? "" : ""}`}
            style={{background: selected?.id===c.id ? 'var(--surface-2)' : 'transparent'}}>
            <span className="text-sm">#{c.name}</span>
            {c.unread>0 && <span className="chip chip-gold">{c.unread}</span>}
          </button>
        ))}
        <div className="label-caps mt-4 mb-2">Direct Messages</div>
        {state.dms.map(c => (
          <button key={c.id} onClick={()=>setSelected(c)} className="w-full text-left py-1.5 px-2 rounded flex justify-between items-center"
            style={{background: selected?.id===c.id ? 'var(--surface-2)' : 'transparent'}}>
            <span className="text-sm">{c.other_user?.name || c.name}</span>
            {c.unread>0 && <span className="chip chip-gold">{c.unread}</span>}
          </button>
        ))}
        <div className="label-caps mt-4 mb-2">Trustees (start DM)</div>
        {users.filter(u => u.status==='approved').map(u => (
          <button key={u.id} onClick={()=>openDM(u.id)} className="w-full text-left py-1 px-2 rounded text-xs" style={{color:'var(--muted)'}}>
            💬 {u.name}
          </button>
        ))}
      </div>
      <div className="flex-1 min-w-0 card p-0 flex flex-col">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-sm" style={{color:'var(--muted)'}}>Select a channel or DM to begin.</div>
        ) : (
          <>
            <div className="p-4 border-b" style={{borderColor:'var(--border)'}}>
              <div className="font-display text-xl">{selected.kind==='dm' ? (selected.other_user?.name||selected.name) : `#${selected.name}`}</div>
              {selected.description && <div className="text-xs" style={{color:'var(--muted)'}}>{selected.description}</div>}
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map(m => (
                <div key={m.id} className="group">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-sm">{m.author_name}</span>
                    <span className="text-xs" style={{color:'var(--muted)'}}>{(m.created_at||"").slice(11,16)}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap font-serif-body">{m.text.split(/(@[\w.\-]+)/).map((p,i)=>p.startsWith("@")?<span key={i} className="chip chip-gold">{p}</span>:<span key={i}>{p}</span>)}</div>
                  {(m.attachments||[]).length>0 && (
                    <div className="mt-2">
                      <MediaGallery assets={(m.attachments||[]).map(id=>mediaMap[id]).filter(Boolean)} canManage={false} compact />
                    </div>
                  )}
                  <div className="flex gap-1 mt-1">
                    {(m.reactions||[]).map(r => (
                      <button key={r.emoji} onClick={()=>react(m.id, r.emoji)} className="text-xs px-2 py-0.5 rounded-full border" style={{borderColor:'var(--border)', background:'var(--surface-2)'}}>{r.emoji} {r.users.length}</button>
                    ))}
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      {["👍","❤️","🎯","✅","🚩"].map(e => <button key={e} onClick={()=>react(m.id, e)} className="text-xs px-1">{e}</button>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t" style={{borderColor:'var(--border)'}}>
              {pending.length>0 && (
                <div className="mb-2 flex flex-wrap gap-2" data-testid="pending-attachments">
                  {pending.map(a => (
                    <span key={a.id} className="flex items-center gap-1 text-xs px-2 py-1 rounded border" style={{borderColor:'var(--border)', background:'var(--surface-2)'}}>
                      <Paperclip size={12}/> {a.original_filename}
                      <button className="ml-1" onClick={()=>setPending(p=>p.filter(x=>x.id!==a.id))}>✕</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2 items-center">
                <MediaUploader contextType="council" contextId={selected.id} visibility="Shared" label="" accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" onUploaded={onAttach} testid="council-uploader" />
                <input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==='Enter' && send()} placeholder="Message (use @name to mention)…" data-testid="msg-input"/>
                <button className="btn-primary" onClick={send} data-testid="msg-send">Send</button>
              </div>
            </div>
          </>
        )}
      </div>
      {showNew && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded max-w-md w-full">
            <div className="font-display text-xl mb-2">Create channel</div>
            <input placeholder="channel-name" value={newCh.name} onChange={e=>setNewCh({...newCh, name:e.target.value})} className="mb-2"/>
            <input placeholder="description" value={newCh.description} onChange={e=>setNewCh({...newCh, description:e.target.value})} className="mb-3"/>
            <div className="flex gap-2 justify-end"><button className="btn-outline" onClick={()=>setShowNew(false)}>Cancel</button><button className="btn-primary" onClick={createChannel}>Create</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== PUBLIC APIs — RATES ROOM ====================
export function RatesRoomPage() {
  const [afr, setAfr] = useState(null);
  const [curve, setCurve] = useState(null);
  const [fx, setFx] = useState(null);
  const [base, setBase] = useState("USD");
  const load = async () => {
    const [a, c, f] = await Promise.all([api.get("/public/afr"), api.get("/public/treasury-curve"), api.get(`/public/fx?base=${base}`)]);
    setAfr(a.data); setCurve(c.data); setFx(f.data);
  };
  useEffect(() => { load(); }, [base]); // eslint-disable-line
  return (
    <div data-testid="rates-page">
      <H1>Rates Room</H1><Sub>Live Treasury averages, AFR proxy, and cross-currency rates. Data from Treasury FiscalData + Frankfurter.</Sub>
      {afr && (
        <Card className="mb-4">
          <div className="label-caps mb-2">AFR proxy · as of {afr.as_of || "—"}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><div className="text-xs" style={{color:'var(--muted)'}}>Short-term (Bills)</div><div className="font-display text-3xl" style={{color:'var(--primary)'}}>{afr.short_term_pct?.toFixed(3)}%</div></div>
            <div><div className="text-xs" style={{color:'var(--muted)'}}>Mid-term (Notes)</div><div className="font-display text-3xl" style={{color:'var(--primary)'}}>{afr.mid_term_pct?.toFixed(3)}%</div></div>
            <div><div className="text-xs" style={{color:'var(--muted)'}}>Long-term (Bonds)</div><div className="font-display text-3xl" style={{color:'var(--primary)'}}>{afr.long_term_pct?.toFixed(3)}%</div></div>
            <div><div className="text-xs" style={{color:'var(--muted)'}}>TIPS</div><div className="font-display text-3xl" style={{color:'var(--gold-hover)'}}>{afr.tips_pct?.toFixed(3)}%</div></div>
          </div>
          <div className="banner-warning mt-3 text-xs">{afr.note}</div>
        </Card>
      )}
      {curve && curve.curve?.length>0 && (
        <Card className="mb-4">
          <div className="label-caps mb-2">Treasury security averages ({curve.date})</div>
          <table><thead><tr><th>Instrument</th><th>Yield</th></tr></thead>
            <tbody>{curve.curve.map(c => (<tr key={c.tenor}><td>{c.tenor}</td><td className="font-mono">{c.yield.toFixed(3)}%</td></tr>))}</tbody></table>
        </Card>
      )}
      {fx && (
        <Card>
          <div className="flex items-center gap-2 mb-2">
            <div className="label-caps">FX · base</div>
            <select value={base} onChange={e=>setBase(e.target.value)} className="max-w-[100px]">
              {["USD","EUR","GBP","CHF","JPY","CAD"].map(c => <option key={c}>{c}</option>)}
            </select>
            <span className="text-xs" style={{color:'var(--muted)'}}>{fx.date}</span>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {Object.entries(fx.rates||{}).slice(0,18).map(([k,v]) => (
              <div key={k} className="p-2 rounded" style={{background:'var(--surface-2)'}}>
                <div className="text-xs" style={{color:'var(--muted)'}}>{k}</div>
                <div className="font-mono">{typeof v === "number" ? v.toFixed(4) : v}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ==================== GRANTS EXPLORER ====================
export function GrantsPage() {
  const [q, setQ] = useState("family trust");
  const [tab, setTab] = useState("open");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const search = async (t = tab) => {
    setBusy(true);
    const ep = t === "open" ? "/public/grants?q=" : "/public/awards?q=";
    const r = await api.get(ep + encodeURIComponent(q));
    setRows(r.data); setBusy(false);
  };
  useEffect(() => { search(); }, []); // eslint-disable-line
  const switchTab = (t) => { setTab(t); setRows([]); search(t); };
  return (
    <div data-testid="grants-page">
      <H1>Grants Explorer</H1><Sub>Live Grants.gov opportunities plus actual USAspending award history — see what gets funded before applying.</Sub>
      <div className="flex gap-2 mb-3">
        <button className={tab==="open"?"btn-primary":"btn-outline"} onClick={()=>switchTab("open")} data-testid="grants-tab-open">Open opportunities</button>
        <button className={tab==="awarded"?"btn-primary":"btn-outline"} onClick={()=>switchTab("awarded")} data-testid="grants-tab-awarded">Awarded (USAspending)</button>
      </div>
      <div className="flex gap-2 mb-4"><input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&search()} className="max-w-lg" data-testid="grants-q"/><button className="btn-primary" onClick={()=>search()} disabled={busy}>Search</button></div>
      {tab==="open" ? (
        <Card className="p-0 overflow-x-auto"><table><thead><tr><th>#</th><th>Title</th><th>Agency</th><th>Closes</th><th></th></tr></thead>
          <tbody>{rows.map(r => (<tr key={r.id}><td className="font-mono text-xs">{r.number}</td><td>{r.title}</td><td className="text-xs">{r.agency}</td><td className="font-mono text-xs">{r.closeDate}</td>
            <td><a href={r.url} target="_blank" rel="noreferrer" className="btn-outline text-xs">Open</a></td></tr>))}</tbody></table></Card>
      ) : (
        <Card className="p-0 overflow-x-auto"><table><thead><tr><th>Recipient</th><th>Amount</th><th>Agency</th><th>Start</th><th>Purpose</th></tr></thead>
          <tbody>{rows.map((r,i) => (<tr key={i}><td className="font-medium">{r.recipient}</td><td className="font-mono text-xs">${Number(r.amount||0).toLocaleString()}</td><td className="text-xs">{r.agency}</td><td className="font-mono text-xs">{r.start}</td><td className="text-xs">{r.description}</td></tr>))}</tbody></table></Card>
      )}
    </div>
  );
}

// ==================== ENTITY LOOKUP (SEC + IRS-EO + OpenCorporates) ====================
export function EntityLookupPage() {
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("eo");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    if (!q) return; setBusy(true); setRows([]);
    const endpoint = tab==="eo" ? "/public/eo?q=" : tab==="sec" ? "/public/sec?name=" : tab==="banks" ? "/public/banks?q=" : "/public/opencorporates?q=";
    const r = await api.get(endpoint + encodeURIComponent(q));
    setRows(r.data); setBusy(false);
  };
  return (
    <div data-testid="lookup-page">
      <H1>Entity Lookup</H1><Sub>Verify counterparties, banks, and public organisations. Data from ProPublica Nonprofit Explorer, SEC EDGAR, OpenCorporates, and FDIC BankFind.</Sub>
      <div className="flex gap-2 mb-3">
        {[["eo","IRS Nonprofit"],["sec","SEC EDGAR"],["oc","OpenCorporates"],["banks","Banks (FDIC)"]].map(([k,l]) => (
          <button key={k} className={tab===k?"btn-primary":"btn-outline"} onClick={()=>{setTab(k);setRows([]);}} data-testid={`lookup-tab-${k}`}>{l}</button>
        ))}
      </div>
      <div className="flex gap-2 mb-4"><input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&search()} placeholder="Entity name…" className="max-w-lg"/><button className="btn-primary" onClick={search} disabled={busy}>Search</button></div>
      {rows.length>0 && (<Card className="p-0 overflow-x-auto"><table>
        <thead><tr>{tab==="eo" ? (<><th>EIN</th><th>Name</th><th>Location</th><th>NTEE</th><th></th></>) :
          tab==="sec" ? (<><th>Title</th><th>Snippet</th><th>Updated</th><th></th></>) :
          tab==="banks" ? (<><th>Bank</th><th>Location</th><th>FDIC Cert</th><th>Active</th><th>Assets ($k)</th><th>Offices</th></>) :
          (<><th>Name</th><th>#</th><th>Jurisdiction</th><th>Status</th><th></th></>)}</tr></thead>
        <tbody>{rows.map((r,i) => tab==="eo" ? (
          <tr key={i}><td className="font-mono text-xs">{r.ein}</td><td>{r.name}</td><td className="text-xs">{r.city}, {r.state}</td><td className="text-xs">{r.ntee_code||"—"}</td><td><a className="btn-outline text-xs" href={r.url} target="_blank" rel="noreferrer">Open</a></td></tr>
        ) : tab==="sec" ? (
          <tr key={i}><td>{r.title}</td><td className="text-xs">{r.summary}</td><td className="font-mono text-xs">{(r.updated||"").slice(0,10)}</td><td><a className="btn-outline text-xs" href={r.link} target="_blank" rel="noreferrer">Open</a></td></tr>
        ) : tab==="banks" ? (
          <tr key={i}><td className="font-medium">{r.name}</td><td className="text-xs">{r.city}, {r.state}</td><td className="font-mono text-xs">{r.cert}</td><td>{r.active ? <span className="chip chip-green text-xs">Insured</span> : <span className="chip chip-amber text-xs">Inactive</span>}</td><td className="font-mono text-xs">{Number(r.assets_k||0).toLocaleString()}</td><td className="text-xs">{r.offices}</td></tr>
        ) : (
          <tr key={i}><td>{r.name}</td><td className="text-xs">{r.number}</td><td>{r.jurisdiction}</td><td>{r.status}</td><td><a className="btn-outline text-xs" href={r.url} target="_blank" rel="noreferrer">Open</a></td></tr>
        ))}</tbody>
      </table></Card>)}
    </div>
  );
}

// ==================== GOVERNMENT AGENCIES DIRECTORY ====================
export function AgenciesPage() {
  const [rows, setRows] = useState(null);
  const [level, setLevel] = useState("");
  useEffect(() => { api.get("/agencies").then(r=>setRows(r.data)); }, []);
  if (!rows) return <div>Loading…</div>;
  const levels = [...new Set(rows.map(r=>r.level))];
  const list = rows.filter(r => !level || r.level===level);
  return (
    <div data-testid="agencies-page">
      <H1>Government Agencies</H1><Sub>Every agency the Grove communicates with — what each one does for the structure, how to reach it, and which portal API already covers it.</Sub>
      <div className="flex gap-2 mb-4 flex-wrap">
        <button className={!level?"btn-primary":"btn-outline"} onClick={()=>setLevel("")}>All ({rows.length})</button>
        {levels.map(l => <button key={l} className={level===l?"btn-primary":"btn-outline"} onClick={()=>setLevel(l)} data-testid={`agency-level-${l.split(' ')[0]}`}>{l}</button>)}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {list.map(a => (
          <Card key={a.id} data-testid={`agency-card-${a.acronym}`}>
            <div className="flex justify-between items-start mb-1">
              <div className="font-display text-lg">{a.name}</div>
              <span className="chip chip-gold text-xs">{a.level}</span>
            </div>
            <div className="font-mono text-xs mb-2" style={{color:'var(--muted)'}}>{a.acronym}{a.phone && a.phone!=="—" ? ` · ${a.phone}` : ""}</div>
            <div className="text-sm mb-3">{a.purpose}</div>
            <div className="flex justify-between items-center">
              {a.website?.startsWith("http")
                ? <a href={a.website} target="_blank" rel="noreferrer" className="btn-outline text-xs">Visit site</a>
                : <span className="text-xs" style={{color:'var(--muted)'}}>{a.website}</span>}
              {a.portal_api && a.portal_api!=="—" && <span className={`chip text-xs ${a.portal_api.startsWith("CONNECTED")?"chip-green":"chip-gray"}`}>{a.portal_api.startsWith("CONNECTED") ? "API connected" : "Register-linked"}</span>}
            </div>
            {a.portal_api && a.portal_api!=="—" && <div className="text-xs mt-2" style={{color:'var(--muted)'}}>{a.portal_api}</div>}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ==================== REGULATORY WATCH ====================
export function RegulatoryPage() {
  const [topic, setTopic] = useState("trust");
  const [fr, setFr] = useState([]); const [cg, setCg] = useState([]); const [sch, setSch] = useState([]);
  const load = async () => {
    const [a,b,c] = await Promise.all([api.get(`/public/federal-register?topic=${encodeURIComponent(topic)}`),
      api.get(`/public/congress?topic=${encodeURIComponent(topic)}`),
      api.get(`/public/scholarly?q=${encodeURIComponent(topic+' governance')}`)]);
    setFr(a.data); setCg(b.data); setSch(c.data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  return (
    <div data-testid="regulatory-page">
      <H1>Regulatory Watch</H1><Sub>Federal Register, Congress, and scholarly signals filtered by topic.</Sub>
      <div className="flex gap-2 mb-4"><input value={topic} onChange={e=>setTopic(e.target.value)} onKeyDown={e=>e.key==='Enter'&&load()} className="max-w-lg"/><button className="btn-primary" onClick={load}>Search</button></div>
      <H2>Federal Register — latest</H2>
      <div className="space-y-2 mb-6">
        {fr.map((r,i) => (<Card key={i} className="p-3">
          <div className="font-medium">{r.title}</div>
          <div className="text-xs" style={{color:'var(--muted)'}}>{r.type} · {r.publication_date} · {(r.agencies||[]).filter(Boolean).join(", ")}</div>
          {r.abstract && <div className="text-sm mt-1">{r.abstract}</div>}
          <a href={r.html_url} target="_blank" rel="noreferrer" className="text-xs underline mt-1 inline-block">Read</a>
        </Card>))}
      </div>
      <H2>Scholarly signal</H2>
      <div className="space-y-2">
        {sch.map((r,i) => (<Card key={i} className="p-3">
          <div className="font-medium">{r.title}</div>
          <div className="text-xs" style={{color:'var(--muted)'}}>{r.year} · {r.authors} · cited by {r.cited_by}</div>
          <a href={r.url} target="_blank" rel="noreferrer" className="text-xs underline">Open</a>
        </Card>))}
      </div>
    </div>
  );
}

// ==================== HOLIDAYS / CALENDAR ====================
export function HolidaysPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const load = async () => { const r = await api.get(`/public/holidays?year=${year}`); setRows(r.data); };
  useEffect(() => { load(); }, [year]); // eslint-disable-line
  return (
    <div data-testid="holidays-page">
      <H1>Calendar & Public Holidays</H1><Sub>U.S. federal holidays used for scheduling filings and meetings.</Sub>
      <div className="flex gap-2 mb-4"><input type="number" value={year} onChange={e=>setYear(+e.target.value)} className="max-w-[120px]"/></div>
      <Card className="p-0 overflow-x-auto"><table><thead><tr><th>Date</th><th>Holiday</th><th>Types</th></tr></thead>
        <tbody>{rows.map(h => (<tr key={h.date}><td className="font-mono">{h.date}</td><td>{h.localName}</td><td className="text-xs">{(h.types||[]).join(", ")}</td></tr>))}</tbody></table></Card>
    </div>
  );
}
